/**
 * PDF processing service for adding billing periods to invoices
 */

import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {fetchAuthSession} from 'aws-amplify/auth'
import {getConfig} from './config'
import {getLogger} from './logging'

const {pdfProcessorApiUrl} = getConfig()
const logger = getLogger('pdfProcessorApi')

export interface AddBillingPeriodRequest {
  pdfBase64: string
  billingPeriodStart: string
  billingPeriodEnd: string
  installmentLabel?: string
  folioId?: string
}

export interface AddBillingPeriodResponse {
  success: boolean
  pdfBase64?: string
  error?: string
  errorCode?: string
  metadata?: {
    originalSize: number
    modifiedSize: number
    processingTime: number
    billingPeriodAdded: string
    timestamp: string
  }
}

export interface PrepareReceiptRequest {
  pdfBase64: string
  ratePlanCode?: string
  totalAmount?: number
  folioId?: string
}

export interface PrepareReceiptResponse {
  success: boolean
  pdfBase64?: string
  error?: string
  errorCode?: string
  metadata?: {
    originalSize: number
    modifiedSize: number
    processingTime: number
    hiltonReimbursementApplied: boolean
    taxesRemoved: boolean
    timestamp: string
  }
}

export const pdfProcessorApi = createApi({
  reducerPath: 'pdfProcessorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: pdfProcessorApiUrl,
    prepareHeaders: async (headers): Promise<Headers> => {
      try {
        const session = await fetchAuthSession()
        const idToken = session.tokens?.idToken?.toString()
        if (idToken) {
          headers.set('Authorization', `Bearer ${idToken}`)
        }
      } catch (e) {
        logger.error({
          message: 'Error occurred getting token for PDF processor API',
          errorData: {error: e}
        })
      }
      return headers
    }
  }),
  tagTypes: [],
  endpoints: builder => ({
    addBillingPeriod: builder.mutation<
      AddBillingPeriodResponse,
      AddBillingPeriodRequest
    >({
      query(request) {
        return {
          url: '/pdf/add-billing-period',
          method: 'POST',
          body: request
        }
      }
    }),
    prepareReceipt: builder.mutation<
      PrepareReceiptResponse,
      PrepareReceiptRequest
    >({
      query(request) {
        return {
          url: '/pdf/prepare-receipt',
          method: 'POST',
          body: request
        }
      }
    })
  })
})

export const {
  useAddBillingPeriodMutation,
  usePrepareReceiptMutation
} = pdfProcessorApi

/**
 * Validates that a string is valid base64
 */
export function isValidBase64(str: string): boolean {
  try {
    return btoa(atob(str)) === str
  } catch {
    return false
  }
}
